import React from "react";

export function stripHtml(html: string): string {
  return html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function isProbablyHtml(s: string): boolean {
  return /<[a-z][\s\S]*>/i.test(s);
}

export function formatBubbleTime(iso?: string): string {
  try {
    const d = iso ? new Date(iso) : new Date();
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

export type ConversationTurn = {
  role: "outbound" | "user" | "model";
  text: string;
  timeIso?: string;
};

function parseTurnTime(raw: unknown): string {
  if (raw == null) return "";
  try {
    const t = new Date(raw as string | number | Date).getTime();
    return Number.isFinite(t) ? new Date(t).toISOString() : "";
  } catch {
    return "";
  }
}

/** Build WhatsApp thread from MessageQueue.logs conversation entries. */
export function extractConversationTurns(log: {
  logs?: unknown;
  body?: string;
  createdAt?: string;
}): ConversationTurn[] {
  const turns: ConversationTurn[] = [];
  if (Array.isArray(log.logs)) {
    for (const entry of log.logs) {
      if (!entry || typeof entry !== "object") continue;
      const o = entry as Record<string, unknown>;
      const role = o.conversationRole;
      const text = typeof o.text === "string" ? o.text.trim() : "";
      if (
        (role === "outbound" || role === "user" || role === "model") &&
        text
      ) {
        turns.push({
          role,
          text,
          timeIso: parseTurnTime(o.timestamp),
        });
      }
    }
  }
  if (turns.length === 0) {
    const body = log.body?.trim();
    if (body && body !== "[skipped]") {
      turns.push({
        role: "outbound",
        text: body,
        timeIso: log.createdAt ?? "",
      });
    }
  }
  return turns;
}

export function WhatsAppConversationThreadPreview({
  turns,
}: {
  turns: ConversationTurn[];
}) {
  return (
    <div className="overflow-hidden rounded-md border border-black/10 shadow-md">
      <div className="flex items-center gap-2 bg-[#008069] px-3 py-2.5 text-white">
        <span className="text-[13px] font-semibold tracking-tight">WhatsApp</span>
        <span className="text-[10px] font-normal opacity-80">conversation</span>
      </div>
      <div
        className="max-h-[min(60vh,24rem)] space-y-2 overflow-y-auto p-3"
        style={{
          backgroundColor: "#efeae2",
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23c8c4bc' fill-opacity='0.35'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      >
        {turns.map((turn, i) => {
          const isCandidate = turn.role === "user";
          const time = formatBubbleTime(turn.timeIso);
          return (
            <div
              key={i}
              className={`flex max-w-[94%] flex-col gap-0.5 ${isCandidate ? "items-start" : "ml-auto items-end"}`}
            >
              <div
                className={`relative rounded-lg px-2.5 py-1.5 text-left shadow-sm ${
                  isCandidate
                    ? "rounded-tl-sm bg-white"
                    : "rounded-tr-sm bg-[#d9fdd3]"
                }`}
                style={{ boxShadow: "0 1px 0.5px rgba(11, 20, 26, 0.13)" }}
              >
                <pre className="whitespace-pre-wrap break-words font-sans text-[13px] leading-snug text-[#111b21]">
                  {turn.text}
                </pre>
              </div>
              <div className="flex items-center gap-1 px-0.5 text-[10px] text-[#667781]">
                <span>{isCandidate ? "Candidate" : turn.role === "outbound" ? "Template" : "AI"}</span>
                {time ? <span>· {time}</span> : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function WhatsAppBubblePreview({
  fullHtml,
  fullPlain,
  sentAt,
}: {
  fullHtml: string | null;
  fullPlain: string;
  sentAt?: string;
}) {
  const time = formatBubbleTime(sentAt);
  return (
    <div className="overflow-hidden rounded-md border border-black/10 shadow-md">
      <div className="flex items-center gap-2 bg-[#008069] px-3 py-2.5 text-white">
        <span className="text-[13px] font-semibold tracking-tight">WhatsApp</span>
        <span className="text-[10px] font-normal opacity-80">preview</span>
      </div>
      <div
        className="max-h-[min(60vh,22rem)] overflow-y-auto p-3"
        style={{
          backgroundColor: "#efeae2",
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23c8c4bc' fill-opacity='0.35'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      >
        <div className="ml-auto flex max-w-[94%] flex-col items-end gap-0.5">
          <div
            className="relative rounded-lg rounded-tr-sm bg-[#d9fdd3] px-2.5 py-1.5 text-left shadow-sm"
            style={{
              boxShadow: "0 1px 0.5px rgba(11, 20, 26, 0.13)",
            }}
          >
            {fullHtml ? (
              <div
                className="max-w-none text-[13px] leading-snug text-[#111b21] [&_a]:text-[#027eb5] [&_a]:underline [&_p]:my-1 [&_br]:block"
                dangerouslySetInnerHTML={{ __html: fullHtml }}
              />
            ) : (
              <pre className="whitespace-pre-wrap break-words font-sans text-[13px] leading-snug text-[#111b21]">
                {fullPlain}
              </pre>
            )}
          </div>
          <div className="flex items-center gap-1 pr-0.5 text-[11px] text-[#667781]">
            <span>{time}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function GenericMessagePreview({
  fullHtml,
  fullPlain,
}: {
  fullHtml: string | null;
  fullPlain: string;
}) {
  return (
    <>
      <div className="border-b border-gray-100 bg-gray-50 px-3 py-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">Message preview</p>
      </div>
      <div className="max-h-[min(70vh,26rem)] overflow-y-auto p-3">
        {fullHtml ? (
          <div
            className="max-w-none text-sm leading-relaxed text-gray-800 [&_a]:text-[#EC6E51] [&_a]:underline [&_p]:my-1.5 [&_br]:block"
            dangerouslySetInnerHTML={{ __html: fullHtml }}
          />
        ) : (
          <pre className="whitespace-pre-wrap break-words font-sans text-sm leading-relaxed text-gray-800">
            {fullPlain}
          </pre>
        )}
      </div>
    </>
  );
}
